import { useTheme } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';

import {
  type ResourceColorName,
  type ResourceColorPalette,
  resourcesColors,
} from '../../../theme/tokens/resourcesColors';
import { resourcesIcons } from '../../../theme/tokens/resourcesIcons';

export type RecordPreviewAction = {
  label: string;
  href: string;
  /** Opens the record in a new tab, so the current task stays where it is. */
  openInNewTab?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
};

export type RecordPreviewProps = {
  /** Resource the record belongs to: sets the icon and the accent. */
  color: ResourceColorName;
  /** What the block shows, e.g. "Élément similaire déjà enregistré". */
  heading: string;
  /** Title or name of the record. Ignored while loading. */
  title?: string;
  /** One line each (date, provider, topic…); empty values are skipped. */
  details?: (string | null | undefined | false)[];
  /** Status of the record, e.g. a state `Tag`. */
  tag?: ReactNode;
  /** Why this record is shown, e.g. the explanation of a match. */
  note?: ReactNode;
  noteIcon?: ReactNode;
  action?: RecordPreviewAction;
  loading?: boolean;
  'data-testid'?: string;
};

export function getVisibleDetails(details: RecordPreviewProps['details']): string[] {
  return (details ?? []).filter((detail): detail is string => typeof detail === 'string' && detail.trim() !== '');
}

export function useRecordPreview(props: RecordPreviewProps) {
  const theme = useTheme();
  const paletteColor = theme.palette[props.color] ?? resourcesColors[props.color];

  const resourceColor: ResourceColorPalette = {
    main: paletteColor?.main ?? resourcesColors[props.color].main,
    light: paletteColor?.light ?? resourcesColors[props.color].light,
    dark: paletteColor?.dark ?? resourcesColors[props.color].dark,
    contrastText: paletteColor?.contrastText ?? resourcesColors[props.color].contrastText,
  };

  return {
    resourceColor,
    IconComponent: theme.resourceIcons?.[props.color] ?? resourcesIcons[props.color],
    details: getVisibleDetails(props.details),
  };
}
